// LeadDetailPage — full lead profile + activity timeline.
// Shows every field on the lead, plus a newest-first timeline of everything
// that's happened to it (auto-logged field changes + manually added notes
// via "Add Activity"). Read-only for the lead's own fields — editing still
// happens through the existing Edit form on the Leads list.

import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Phone, Mail, MapPin, IndianRupee, Building2, UserCheck, User,
  History, Plus, Send, Loader2, MessageSquare, X,
} from "lucide-react";
import api from "../api/api";
import { getActionMeta, formatActivityTime } from "../utils/activityMeta";
import "../css/sitevisit/activitylog.css";
import "../css/leads/leaddetails.css";

const STATUS_LABELS = {
  new: "New", contacted: "Contacted", interested: "Interested",
  follow_up: "Follow-up", matured: "Matured", closed: "Closed", lost: "Lost",
};
const STATUS_CLASS = {
  new: "ld-status-new", contacted: "ld-status-contacted", interested: "ld-status-interested",
  follow_up: "ld-status-followup", matured: "ld-status-matured", closed: "ld-status-closed", lost: "ld-status-lost",
};
const INTEREST_CLASS = { cold: "ld-interest-cold", warm: "ld-interest-warm", hot: "ld-interest-hot" };

function formatBudget(val) {
  if (!val && val !== 0) return null;
  const n = typeof val === "number" ? val : parseFloat(String(val).replace(/[^0-9.]/g, ""));
  if (isNaN(n) || n === 0) return null;
  if (n >= 10_000_000) return `₹${parseFloat((n / 10_000_000).toFixed(2))} Cr`;
  if (n >= 100_000)    return `₹${parseFloat((n / 100_000).toFixed(2))} L`;
  if (n >= 1_000)      return `₹${parseFloat((n / 1_000).toFixed(1))} K`;
  return `₹${n}`;
}

export default function LeadDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [lead,    setLead]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState("");

  const [showAddActivity, setShowAddActivity] = useState(false);
  const [activityText,    setActivityText]    = useState("");
  const [savingActivity,  setSavingActivity]  = useState(false);

  const fetchLead = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/leads/${id}`);
      setLead(res.data);
      setError("");
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load lead");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLead(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAddActivity = async () => {
    const remarks = activityText.trim();
    if (!remarks) return;
    setSavingActivity(true);
    try {
      const res = await api.post(`/leads/${id}/activity`, { remarks });
      setLead(res.data.lead);
      setActivityText("");
      setShowAddActivity(false);
    } catch (err) {
      alert(err.response?.data?.message || "Failed to add activity");
    } finally {
      setSavingActivity(false);
    }
  };

  if (loading) return <p className="page-loading">Loading lead...</p>;
  if (error)   return <p className="page-error">{error}</p>;
  if (!lead)   return <p className="page-error">Lead not found</p>;

  const timeline = [...(lead.activityLog || [])]
    .sort((a, b) => new Date(b.changedAt) - new Date(a.changedAt));

  return (
    <div className="lead-details-page">
      {/* HEADER */}
      <div className="details-header">
        <button className="back-btn" onClick={() => navigate(-1)}>
          <ArrowLeft size={18} /> Back
        </button>
        <h2>Lead Details</h2>
      </div>

      <div className="details-card">

        {/* LEAD SUMMARY */}
        <div className="details-section ld-summary-row">
          <div className="ld-avatar">{lead.full_name?.charAt(0).toUpperCase() || "?"}</div>
          <div>
            <p className="title">{lead.full_name}</p>
            <div className="badges">
              <span className={`status ${STATUS_CLASS[lead.status] || ""}`}>
                {STATUS_LABELS[lead.status] || lead.status || "Unknown"}
              </span>
              {lead.lead_type && <span className="ld-badge-secondary">{lead.lead_type}</span>}
              {lead.interest_level && (
                <span className={`ld-badge-interest ${INTEREST_CLASS[lead.interest_level] || ""}`}>
                  {lead.interest_level} interest
                </span>
              )}
            </div>
          </div>
        </div>

        {/* CONTACT & DETAILS */}
        <div className="details-section grid">
          <div className="info-box">
            <Phone size={16} />
            <div><label>Phone</label><p><a href={`tel:${lead.phone}`}>{lead.phone || "—"}</a></p></div>
          </div>

          {lead.email && (
            <div className="info-box">
              <Mail size={16} />
              <div><label>Email</label><p><a href={`mailto:${lead.email}`}>{lead.email}</a></p></div>
            </div>
          )}

          {(lead.budget_min || lead.budget_max) && (
            <div className="info-box">
              <IndianRupee size={16} />
              <div>
                <label>Budget</label>
                <p>{formatBudget(lead.budget_min) || "—"} – {formatBudget(lead.budget_max) || "—"}</p>
              </div>
            </div>
          )}

          {lead.preferred_locations?.length > 0 && (
            <div className="info-box">
              <MapPin size={16} />
              <div><label>Preferred Locations</label><p>{lead.preferred_locations.join(", ")}</p></div>
            </div>
          )}

          <div className="info-box">
            <Building2 size={16} />
            <div>
              <label>Property</label>
              <p>
                {lead.propertyId?.propertyName || lead.propertyName
                  ? (lead.propertyId?.propertyName || lead.propertyName)
                  : <span className="unassigned-label">No property linked</span>}
              </p>
            </div>
          </div>

          <div className="info-box">
            <UserCheck size={16} />
            <div>
              <label>Assigned Agent</label>
              <p>{lead.assignedToEmail || <span className="unassigned-label">Unassigned</span>}</p>
            </div>
          </div>

          {lead.source && (
            <div className="info-box">
              <User size={16} />
              <div><label>Source</label><p>{lead.source}{lead.source_other_reason ? ` — ${lead.source_other_reason}` : ""}</p></div>
            </div>
          )}
        </div>

        {/* NOTES */}
        {lead.notes && (
          <div className="details-section">
            <h3><MessageSquare size={18} /> Notes</h3>
            <p className="feedback">{lead.notes}</p>
          </div>
        )}

        {/* ACTIVITY TIMELINE */}
        <div className="details-section">
          <div className="ld-timeline-header">
            <h3><History size={18} /> Activity Timeline</h3>
            <button
              className="ld-add-activity-btn"
              onClick={() => setShowAddActivity((v) => !v)}
            >
              {showAddActivity ? <X size={14} /> : <Plus size={14} />}
              {showAddActivity ? "Cancel" : "Add Activity"}
            </button>
          </div>

          {showAddActivity && (
            <div className="ld-add-activity-form">
              <textarea
                autoFocus
                placeholder={'What happened? e.g. "She will come for site visit this Saturday."'}
                value={activityText}
                onChange={(e) => setActivityText(e.target.value)}
                rows={3}
              />
              <div className="ld-add-activity-actions">
                <button
                  className="ld-btn-secondary"
                  onClick={() => { setShowAddActivity(false); setActivityText(""); }}
                >
                  Cancel
                </button>
                <button
                  className="ld-btn-primary"
                  onClick={handleAddActivity}
                  disabled={!activityText.trim() || savingActivity}
                >
                  {savingActivity ? <Loader2 size={14} className="ld-spin" /> : <Send size={14} />}
                  Save
                </button>
              </div>
            </div>
          )}

          {timeline.length === 0 ? (
            <p className="al-empty">No activity recorded yet.</p>
          ) : (
            <ul className="al-timeline">
              {timeline.map((e, i) => {
                const meta = getActionMeta(e.action);
                return (
                  <li key={i} className="al-item">
                    <span className={"al-icon " + meta.cls}>{meta.icon}</span>
                    <div className="al-content">
                      <div className="al-line">
                        <strong>{meta.label}</strong>
                        {e.field && e.action !== "created" && (
                          <span className="al-field"> · {e.field}</span>
                        )}
                      </div>
                      {e.action === "note" ? (
                        e.newValue && <p className="al-note-text">{e.newValue}</p>
                      ) : (
                        e.action !== "created" && (e.oldValue || e.newValue) && (
                          <div className="al-change">
                            <span className="al-old">{e.oldValue || "—"}</span>
                            <span className="al-arrow">→</span>
                            <span className="al-new">{e.newValue || "—"}</span>
                          </div>
                        )
                      )}
                      <div className="al-meta">
                        {e.changedByName || e.changedByEmail || "Unknown"}
                        <span className="al-dot">•</span>
                        {formatActivityTime(e.changedAt)}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* META */}
        {lead.createdByEmail && (
          <div className="details-section muted small">
            Created by: <strong>{lead.createdByEmail}</strong>
            {lead.createdDate && <> · {formatActivityTime(lead.createdDate)}</>}
          </div>
        )}

      </div>
    </div>
  );
}
